/**
 * Point annotation geometry properties — registration and getters.
 *
 * These properties store the geometric data for Point annotations,
 * replacing the old action-based system.
 *
 * Properties registered here:
 * - point_position (COORDINATE, PROJECT_GLOBAL) — {x, y, z}
 * - point_pick_color (COLOR, PROJECT_GLOBAL) — hex string from point cloud
 * - point_classification (INT, PROJECT_GLOBAL) — classification code
 * - point_intensity (FLOAT, PROJECT_GLOBAL) — intensity value
 * - point_pointcloud_id (STRING, PROJECT_GLOBAL) — source pointcloud id
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

export function registerPointGeometryProperties() {
  if (!propertyRegistry.isRegistered('point_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'point_position',
      name: 'Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('point_pick_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'point_pick_color',
      name: 'Pick Color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#ffffff',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('point_classification')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'point_classification',
      name: 'Classification',
      valueType: PropertyValueType.INT,
      defaultValue: 0,
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('point_intensity')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'point_intensity',
      name: 'Intensity',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('point_pointcloud_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'point_pointcloud_id',
      name: 'Point Cloud ID',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [],
    });
  }
}

// Register immediately on import
registerPointGeometryProperties();

// ============================================================================
// GETTERS (uses global property service)
// ============================================================================

export function getPointPositionProperty(
  annotationId: string,
): ReactiveProperty<{ x: number; y: number; z: number }> {
  const service = getGlobalPropertyService();
  return service.getProperty(
    'point_position',
    annotationId,
    PropertyType.PROJECT_GLOBAL,
  ) as ReactiveProperty<{ x: number; y: number; z: number }>;
}

export function getPointPickColorProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty(
    'point_pick_color',
    annotationId,
    PropertyType.PROJECT_GLOBAL,
  ) as ReactiveProperty<string>;
}

export function getPointClassificationProperty(annotationId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty(
    'point_classification',
    annotationId,
    PropertyType.PROJECT_GLOBAL,
  ) as ReactiveProperty<number>;
}

export function getPointIntensityProperty(annotationId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty(
    'point_intensity',
    annotationId,
    PropertyType.PROJECT_GLOBAL,
  ) as ReactiveProperty<number>;
}

export function getPointPointcloudIdProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty(
    'point_pointcloud_id',
    annotationId,
    PropertyType.PROJECT_GLOBAL,
  ) as ReactiveProperty<string>;
}
